import React from "react";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import PeopleField from "../components/PeopleField";
import { BaseSessionDraft, SessionStepProps } from "../types";

const MainInfoStep: React.FC<SessionStepProps<BaseSessionDraft>> = ({
  data,
  setData,
  errors,
  corporateId,
  disabledFields,
  datesDisabledReason,
  startMinDate,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box>
        <Grid container spacing={2} sx={{ mt: "10px" }}>
          <Grid item xs={12} md={6}>
            <TimePickerCst
              id="session-start-date"
              label={String(t("main.start-date"))}
              value={data.start_date ? dayjs(data.start_date) : null}
              onChange={(v: any) => {
                if (v && !dayjs(v).isValid()) return;
                setData({ start_date: v ? dayjs(v).toISOString() : null });
              }}
              required
              disabled={disabledFields.has("start_date")}
              minDate={
                startMinDate ? dayjs(startMinDate).toISOString() : undefined
              }
              error={!!errors.start_date}
              errorMessage={errors.start_date}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePickerCst
              id="session-end-date"
              label={String(t("main.end-date"))}
              value={data.end_date ? dayjs(data.end_date) : null}
              onChange={(v: any) => {
                if (v && !dayjs(v).isValid()) return;
                setData({ end_date: v ? dayjs(v).toISOString() : null });
              }}
              required
              disabled={disabledFields.has("end_date")}
              minDate={data.start_date || undefined}
              error={!!errors.end_date}
              errorMessage={errors.end_date}
            />
          </Grid>
        </Grid>
        {datesDisabledReason && (
          <Typography
            sx={{ fontSize: "11px", color: theme.palette.warning.main, mt: 1 }}
          >
            {datesDisabledReason}
          </Typography>
        )}
      </Box>

      <PeopleField
        label={String(t("main.faculties"))}
        value={data.faculties}
        emptyLabel={String(t("main.faculties-empty"))}
        pickerTitle={String(t("main.faculties-picker"))}
        corporateId={corporateId}
        roleFilter="role=TEACHER"
        onChange={(faculties) => setData({ faculties })}
      />

      <PeopleField
        label={String(t("main.tutors"))}
        value={data.tutors}
        emptyLabel={String(t("main.tutors-empty"))}
        pickerTitle={String(t("main.tutors-picker"))}
        corporateId={corporateId}
        roleFilter="role=TUTOR"
        onChange={(tutors) => setData({ tutors })}
      />
    </Box>
  );
};

export default MainInfoStep;
